/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React from 'react'
import { Tooltip } from 'antd'
import { LinkOutlined, PictureOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
// import SendViaIcon from './SendViaIcon'

/**
 * @interface FeedPostModalIconButtonsProps
 * @property {boolean} showImage - Whether the image upload section is currently visible
 * @property {boolean} showLink - Whether the link input section is currently visible
 * @property {(section: 'image' | 'link' | 'sendVia') => void} onToggle - Callback to toggle visibility of image or link sections
 * @property {boolean} sendViaSelected - Whether the send via section is currently active/visible
 * @property {() => void} onSendViaClick - Callback executed when the send via button is clicked
 */
interface FeedPostModalIconButtonsProps {
  showImage: boolean
  showLink: boolean
  onToggle: (section: 'image' | 'link' | 'sendVia') => void
  sendViaSelected?: boolean
  onSendViaClick?: () => void
}

/**
 * Icon buttons component for the feed post modal - handles image, link, and send via toggles
 *
 * This component renders a horizontal row of three toggle buttons that control the visibility
 * of different sections in the post creation modal:
 * - Image button: Toggles the image upload section
 * - Link button: Toggles the URL link input section
 * - Send Via button: Toggles the email/push notification options section
 *
 * Each button changes color based on its active state and includes tooltips for better UX.
 * The component is memoized to prevent unnecessary re-renders when parent state changes.
 *
 * @param props - Component props as defined in FeedPostModalIconButtonsProps
 * @returns JSX element representing a row of toggle buttons for post creation options
 */
const FeedPostModalIconButtons: React.FC<FeedPostModalIconButtonsProps> =
  React.memo(({ showImage, showLink, onToggle }) => {
    return (
      <div
        css={css`
          justify-content: flex-start;
          align-items: center;
          gap: 8px;
          display: flex;
        `}
      >
        <Tooltip title='Add Image' zIndex={100000}>
          <div
            onClick={() => onToggle('image')}
            css={css`
              padding: 8px;
              background: #1b222b;
              border-radius: 8px;
              display: flex;
              align-items: center;
              cursor: pointer;
              border: none;
            `}
          >
            <PictureOutlined
              style={{
                color: showImage ? colors.SECONDARY_100 : colors.PRIMARY,
                fontSize: 24,
              }}
            />
          </div>
        </Tooltip>
        <Tooltip title='Add Link' zIndex={100000}>
          <div
            onClick={() => onToggle('link')}
            css={css`
              padding: 8px;
              background: #1b222b;
              border-radius: 8px;
              display: flex;
              align-items: center;
              cursor: pointer;
              border: none;
            `}
          >
            <LinkOutlined
              style={{
                color: showLink ? colors.SECONDARY_100 : colors.PRIMARY,
                fontSize: 24,
              }}
            />
          </div>
        </Tooltip>
        {/* <Tooltip title='Also Send Via' zIndex={100000}>
            <div
              onClick={onSendViaClick}
              css={css`
                padding: 8px;
                background: #1b222b;
                border-radius: 8px;
                display: flex;
                align-items: center;
                cursor: pointer;
                border: none;
              `}
            >
              <SendViaIcon selected={sendViaSelected} />
            </div>
          </Tooltip> */}
      </div>
    )
  })

export default FeedPostModalIconButtons
